import { useLocation, Link } from "react-router-dom";
import { Home, Compass, Image, Heart, LogOut } from "lucide-react";
import { cn } from "@/lib/utils";
import { useUser } from "@/contexts/UserContext";

const navItems = [
  { path: "/", icon: Home, label: "Home" },
  { path: "/bucketlist", icon: Compass, label: "Adventures" },
  { path: "/memories", icon: Image, label: "Memories" },
  { path: "/characters", icon: Heart, label: "Characters" },
];

function PawDot() {
  return (
    <svg width="12" height="10" viewBox="0 0 24 20" className="mt-0.5">
      <ellipse cx="12" cy="14" rx="4.5" ry="3.5" fill="#E8A0B4" />
      <circle cx="7" cy="7" r="2.5" fill="#E8A0B4" />
      <circle cx="17" cy="7" r="2.5" fill="#E8A0B4" />
      <circle cx="4.5" cy="12" r="2" fill="#E8A0B4" />
      <circle cx="19.5" cy="12" r="2" fill="#E8A0B4" />
    </svg>
  );
}

export default function Layout({ children }) {
  const location = useLocation();
  const { logout } = useUser();
  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-cream font-sans">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex fixed left-0 top-0 h-full w-60 bg-white shadow-soft flex-col z-40 border-r border-border-pink">
        <div className="p-8 pb-6">
          <h1 className="font-heading text-2xl text-text-primary tracking-tight">
            Waimbow
          </h1>
          <p className="text-xs text-text-secondary mt-1">
            Mama's adventure journal
          </p>
        </div>
        <nav className="flex-1 px-4 space-y-1">
          {navItems.map(({ path, icon: Icon, label }) => (
            <Link
              key={path}
              to={path}
              data-testid={`sidebar-nav-${label.toLowerCase()}`}
              className={cn(
                "flex items-center gap-3 px-4 py-3 rounded-pill transition-colors text-sm font-medium",
                isActive(path)
                  ? "bg-blush/10 text-blush-dark"
                  : "text-text-secondary hover:bg-cream hover:text-text-primary"
              )}
            >
              <Icon size={20} />
              {label}
            </Link>
          ))}
        </nav>
        <div className="px-4 pb-6">
          <button
            onClick={logout}
            className="flex items-center gap-3 px-4 py-3 rounded-pill transition-colors text-sm font-medium text-text-secondary hover:bg-cream hover:text-text-primary w-full"
            data-testid="sidebar-logout-btn"
          >
            <LogOut size={18} /> Log out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="pb-24 md:pb-8 md:ml-60">
        <div className="px-5 md:px-10 pt-6 md:pt-8 max-w-[800px] mx-auto">
          {children}
        </div>
      </main>

      {/* Mobile Bottom Nav */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 h-16 bg-white border-t border-border-pink flex items-center justify-around z-40"
        data-testid="bottom-nav"
      >
        {navItems.map(({ path, icon: Icon, label }) => (
          <Link
            key={path}
            to={path}
            data-testid={`nav-${label.toLowerCase()}`}
            className="flex flex-col items-center min-w-[60px]"
          >
            <Icon
              size={22}
              className={cn(
                "transition-colors",
                isActive(path) ? "text-blush" : "text-text-secondary"
              )}
            />
            <span
              className={cn(
                "text-[10px] font-medium mt-0.5",
                isActive(path) ? "text-blush-dark" : "text-text-secondary"
              )}
            >
              {label}
            </span>
            {isActive(path) ? (
              <PawDot />
            ) : (
              <div className="h-[10px]" />
            )}
          </Link>
        ))}
      </nav>
    </div>
  );
}
